import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookingMethods, IBookingTime, IInfoAtTime, ITableItem } from 'shared-types/SharedTypes';
import { IBookingInfoAtTime, IServiceItemSubset } from 'external-services/booking-availability-service/types';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { DiaryRootState } from 'appRoot/rootReducer';
import { LocaleService } from 'internal-services/locale/locale.service';
import {IAvailableTimesSimple, IBookingTimeState} from './types';
import { IDiarySchedule, IScheduleInterval } from 'shared-types/DiaryTypes';
import { displayActions } from 'booking-creator/reducers/display/slice';
import TimeSelectionService from 'booking-creator/services/time-selection-service';
import { activeSectionsActions } from 'booking-creator/reducers/activeSections/slice';
import { BookingMode } from 'booking-creator/reducers/display/types';
import { cloneDeep } from 'lodash';
import {
  getSelectedInfoAtTime,
  getSelectedInfoAtTimeForEditBooking,
  getSelectedInfoAtTimeForNewBooking,
} from 'booking-creator/reducers/bookingTimes/helper';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { TimeMode } from 'booking-creator/components/TimeSelector/types';
import {AppDispatch} from "appRoot/Store";

const NS = 'bookingTimesSlice';

export const initialState: IBookingTimeState = {
  availableTimes: [],
  availableTimesSimple: [],
  selectedInfoAtTime: null,
  previouslySelectedInfoAtTime: null,
  coversBookingIntervals: [],
  tableSeaterWarningMessages: [],
  timeSelectorMode: TimeMode.predefined,
  showTimeInBookingShortSummary: false,
  skipDefaultSelection: false
};

export const getBookingTimes = createAsyncThunk(
  'bookingTimes/getBookingTimes',
  async (
    // cancelCB can be passed if you need to cancel execution midway through the function
    payload: {keepSelectedTime: boolean, cancelCB?: (calleeID?: string) => boolean},
    { dispatch, getState }
  ): Promise<{
    availableTimesSimple: IAvailableTimesSimple[];
    availableTimes: IInfoAtTime[];
    coversBookingIntervals: IScheduleInterval[];
    selectedInfoAtTime: IBookingTime;
  }> => {
    const {
      displayReducer: { activeBooking, selectedService, activeSchedule, mode },
      activeLayoutsReducer: { layouts },
      activeSectionsReducer: { bookingsByTable, tablesListForActiveService },
      settingReducer: {
        generalSettings: { enableMaxOccupancy },
      },
      venueReducer: { timeZoneId },
    } = getState() as DiaryRootState;
    const schedule: IDiarySchedule = activeSchedule;

    const isNewBooking = mode === BookingMode.newBooking;
    const isEditBooking = mode === BookingMode.editBooking;
    const isViewModeBooking = mode === BookingMode.viewBooking;

    const tablesMutable = BookingAvailabilityService.getMutableTables(tablesListForActiveService);
    const bookingsByTableMutable =
      BookingAvailabilityService.getMutableBookingsByTable(bookingsByTable);

    let availableTimes: IBookingInfoAtTime[] = [];

    try {
      availableTimes = BookingAvailabilityService.calulateBookingTimes(
        schedule,
        selectedService as IServiceItemSubset,
        cloneDeep(layouts), // gets modified, so needs to be cloned
        activeBooking.duration as number,
        activeBooking.people,

        tablesMutable, // needs to be mutable
        bookingsByTableMutable, // needs to be mutable

        activeBooking.preferredSectionId,
        null, // tableId is always null for Diary Mobile, as there is no timeline or floorplan to click
        false,
        activeBooking.method,
        true,
        activeBooking.time
      );
    } catch (err) {
      console.warn(NS, 'error calling BookingAvailabilityService.calulateBookingTimes', err);
    }

    const coversBookingIntervals: IScheduleInterval[] = [];
    if (enableMaxOccupancy) {
      /**
       * @todo: move BookingIntervalService to submodule and finish this part off
       */
      // const timesForIntervals = availableTimes.map(t => ({time: t.time}));
      // coversBookingIntervals = BookingIntervalService.getCoversPerMinuteInterval(timesForIntervals, activeScheduleBookings, activeSections as ISectionMaxOcc[]);
    }

    // after BookingAvailabilityService mutations, we must set it again in the reducer
    dispatch(activeSectionsActions.setTablesListForActiveService(tablesMutable));


    const isWalkin = activeBooking.method === BookingMethods.walkin;
    const isNewMode = mode === BookingMode.newBooking;
    let nowVenueTime: Date;

    // if (isNewMode) {
    //   nowVenueTime = isWalkin
    //     ? TimeSelectionService.getNowVenueTime(activeBooking.time, timeZoneId)
    //     : activeBooking.time;
    // }

    let selectedInfoAtTime: IBookingTime;
    let selectedTableIds: string[] = [];
    if (availableTimes.length) {
      if (isNewBooking) {
        const result = await getSelectedInfoAtTimeForNewBooking(
          getState() as DiaryRootState,
          dispatch as AppDispatch,
          availableTimes,
          tablesMutable,
          payload.keepSelectedTime
        );

        if (payload.cancelCB && payload.cancelCB(`getBookingTimes-1`)) return null;

        selectedTableIds = result.selectedTableIds;
        selectedInfoAtTime = result.selectedInfoAtTime;
        if (isWalkin) {
          selectedInfoAtTime.time = TimeSelectionService.getNowVenueTime(
            activeBooking.time,
            timeZoneId
          );
        }
      } else if (isEditBooking) {
        const result = await getSelectedInfoAtTimeForEditBooking(
          getState() as DiaryRootState,
          availableTimes,
          tablesMutable
        );

        if (payload.cancelCB && payload.cancelCB(`getBookingTimes-2`)) return null;

        selectedTableIds = result.selectedTableIds;
        selectedInfoAtTime = result.selectedInfoAtTime;
        if (result.isNotInList) {
          selectedInfoAtTime.isManualTime = true;
          availableTimes.unshift(selectedInfoAtTime as any);
        }
      }

      if (isViewModeBooking && isWalkin) {
        const result = await getSelectedInfoAtTimeForNewBooking(
          getState() as DiaryRootState,
          dispatch as AppDispatch,
          availableTimes,
          tablesMutable,
          payload.keepSelectedTime
        );

        if (payload.cancelCB && payload.cancelCB(`getBookingTimes-3`)) return null;

        selectedTableIds = result.selectedTableIds;
        selectedInfoAtTime = result.selectedInfoAtTime;
        selectedInfoAtTime.time = TimeSelectionService.getNowVenueTime(
          activeBooking.time,
          timeZoneId
        );
      }

      /**
       * checks for activeBooking.time here as well, so it doesn't auto select the time
       * if it hasn't been manually selected already
       */
      if (selectedInfoAtTime && activeBooking.time) {
        await dispatch(displayActions.updateActiveBookingTime(selectedInfoAtTime.time));
        if (payload.cancelCB && payload.cancelCB(`getBookingTimes-4`)) return null;
      }

      dispatch(activeSectionsActions.setSelectedTableIds(selectedTableIds));
    }

    const availableTimesSimple = availableTimes.map(at => ({
      time: at.time,
      open: at.open,
      maxCovers: at.maxCovers,
      coversReached: at.coversReached,
      status: at.status
    }))
    return { availableTimesSimple, availableTimes, coversBookingIntervals, selectedInfoAtTime };
  }
);

export const setSelectedInfoAtTime = createAsyncThunk(
  'bookingTimes/setSelectedInfoAtTime',
  async (
    payload: Date,
    { dispatch, getState }
  ): Promise<{
    selectedInfoAtTime: IBookingTime;
    previouslySelectedInfoAtTime: IBookingTime;

    availableTimes: IInfoAtTime[];
    // newSelectedTablesList: ITableItem[];
    newTime: Date;
    // newStatus: IBookingStatus;
    // selectedTableIds?: string[];
    selectFromTablesOptions?: ITableItem[];
  }> => {
    const {
      displayReducer: { activeBooking, mode },
      tablesReducer: { hasUserDefinedTables },
      bookingTimesReducer: { availableTimes },
      activeSectionsReducer: { tablesListForActiveService },
      venueReducer: { timeZoneId },
    } = getState() as DiaryRootState;

    const isEditBooking = mode === BookingMode.editBooking;

    const selectedInfoAtTimeWithTable: IBookingTime = availableTimes.reduce(
      (acc: IBookingTime[], avTm) => {
        if (avTm.time.getTime() === payload.getTime()) {
          acc.push({ ...avTm, table: null });
        }
        return acc;
      },
      []
    )[0];

    let newSelectedTime: IBookingTime;
    let previouslySelectedInfoAtTime: IBookingTime;
    let newSelectedTablesList: ITableItem[];
    let availableTablesList: ITableItem[];
    const hasTableSelection = !!activeBooking.tables.length;
    try {
      const tableIds = activeBooking.tables.map(i => i._id)
      const result = await TimeSelectionService.addTableToSelectedInfoAtTime({
        selectedTime: payload,
        selectedInfoAtTime: selectedInfoAtTimeWithTable,
        autoSelectTable: true,
        hasUserDefinedTables,
        activeBookingMethod: activeBooking.method,
        activeBookingType: activeBooking.bookingType,
        tablesList: tablesListForActiveService,
        tableIds, // will always be empty for Diary Mobile because there is no floorplan or timeline to selecta table
        isCustomTime: false, // used for walkins that can enter a custom time. @todo: get this working when doing walkin logic
        isEditBooking,
        activePax: activeBooking.people,
      });

      newSelectedTime = result.newSelectedTime;
      previouslySelectedInfoAtTime = result.previouslySelectedInfoAtTime;
      newSelectedTablesList = result.newSelectedTablesList;
      availableTablesList = result.availableTablesList;
    } catch (err) {
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem selecting time',
          errorMessage: `There was an error trying to select ${payload.toLocaleString(LocaleService.getLocale())}.`,
          accordionDetail: getAccordionDetail(err),
        })
      );
    }

    dispatch(displayActions.updateActiveBookingTime(payload));
    dispatch(activeSectionsActions.setSelectedTableIds(availableTablesList.map((t) => t._id)));
    dispatch(activeSectionsActions.setTablesListForActiveService(newSelectedTablesList));

    // @todo: review for walkins
    // availableTimes = TimeSelectionService.updateBookingTimes(newAvailableTime, cloneDeep(availableTimes));

    return {
      selectedInfoAtTime: newSelectedTime,
      previouslySelectedInfoAtTime,
      newTime: newSelectedTime.time,
      availableTimes,
      // newSelectedTablesList,
      // selectedTableIds: availableTablesList.map(t => t._id)
    };
  }
);

export const updateManualTimeToSelectedInfoAtTime = createAsyncThunk(
  'bookingTimes/updateManualTimeToSelectedInfoAtTime',
  async (payload, { dispatch, getState }): Promise<IBookingTime> => {
    return getSelectedInfoAtTime(getState() as DiaryRootState);
  }
);

const bookingTimesSlice = createSlice({
  name: 'bookingTimes', // used in action types
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    setSelectedTime: (state: IBookingTimeState, action: PayloadAction<IBookingTime>) => {
      state.selectedInfoAtTime = action.payload;
    },
    setTableSeaterWarningMessages: (state: IBookingTimeState, action: PayloadAction<string[]>) => {
      state.tableSeaterWarningMessages = action.payload;
    },
    setAvailableTimes: (state: IBookingTimeState, action: PayloadAction<IInfoAtTime[]>) => {
      state.availableTimes = action.payload;
    },
    setTimeSelectorMode: (state: IBookingTimeState, action: PayloadAction<TimeMode>) => {
      state.timeSelectorMode = action.payload;
    },
    setShowTimeInBookingShortSummary: (state: IBookingTimeState, action: PayloadAction<boolean>) => {
      state.showTimeInBookingShortSummary = action.payload;
    },
    setSkipDefaultSelection: (state: IBookingTimeState, action: PayloadAction<boolean>) => {
      state.skipDefaultSelection = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getBookingTimes.fulfilled, (state, action) => {
      if (!action.payload) return;

      const { availableTimesSimple, availableTimes, selectedInfoAtTime } = action.payload;
      state.availableTimesSimple = availableTimesSimple; //only contains the data we need for TimeSelector
      state.availableTimes = availableTimes;
      state.selectedInfoAtTime = selectedInfoAtTime;
    });

    builder.addCase(setSelectedInfoAtTime.fulfilled, (state, action) => {
      state.selectedInfoAtTime = action.payload.selectedInfoAtTime;
      state.previouslySelectedInfoAtTime = action.payload.previouslySelectedInfoAtTime;
      state.availableTimes = action.payload.availableTimes;
    });

    builder.addCase(updateManualTimeToSelectedInfoAtTime.fulfilled, (state, action) => {
      state.selectedInfoAtTime = action.payload;
    });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = bookingTimesSlice;
// Extract and export each action creator by name
export { actions as bookingTimesActions };
// Export the reducer, either as a default or named export
export default reducer;
